import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { 
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon
 } from '@mui/icons-material';

import { GeoPackageType } from '../../models/types/geoPackageType';
import { CorruptAsset, CorruptPlan, GeoPackageResult, PackageError } from '../../models/types/geoPackageResult';



interface GeoPackageValidationResultProps {
  type: GeoPackageType;
  result?: GeoPackageResult;
  success: boolean;
  packageErrors?: PackageError[];
}

const getConfig = (type: GeoPackageType) => {
  if (type === GeoPackageType.ASSET) {
    return {
      labelPrefix: 'assets',
      entityLabel: 'assets',
      corruptKey: 'assetsCorrupt',
      corruptCountKey: 'assetsCorruptCount',
    };
  }

  return {
    labelPrefix: 'plans',
    entityLabel: 'plannen',
    corruptKey: 'plansCorrupt',
    corruptCountKey: 'plansCorruptCount',
  };
};

const getSections = (result: GeoPackageResult, type: GeoPackageType) => {
  const prefix = type === GeoPackageType.ASSET ? 'assets' : 'plans';
  const label = type === GeoPackageType.ASSET ? 'objecten' : 'plannen';

  return [
    {
      label: `Aantal nieuwe ${label}`,
      count: result[`${prefix}ToAddCount` as keyof GeoPackageResult] as number ?? 0,
      items: result[`${prefix}ToAdd` as keyof GeoPackageResult] as string[] ?? [],
    },
    {
      label: `Aantal te verwijderen ${label}`,
      count: result[`${prefix}ToRemoveCount` as keyof GeoPackageResult] as number ?? 0,
      items: result[`${prefix}ToRemove` as keyof GeoPackageResult] as string[] ?? [],
    },
    {
      label: `Aantal gewijzigde ${label}`,
      count: result[`${prefix}ToUpdateCount` as keyof GeoPackageResult] as number ?? 0,
      items: result[`${prefix}ToUpdate` as keyof GeoPackageResult] as string[] ?? [],
    },
    {
      label: `Aantal ${label} zonder wijziging`,
      count: result[`${prefix}WithoutChangesCount` as keyof GeoPackageResult] as number ?? 0,
      items: result[`${prefix}WithoutChanges` as keyof GeoPackageResult] as string[] ?? [],
    },
  ];
};

const GeoPackageValidationResult: React.FC<GeoPackageValidationResultProps> = ({ type, result, success, packageErrors }) => {
  const config = getConfig(type);

  // console.log('result', result)

  const corruptItems =
    result?.[config.corruptKey as keyof GeoPackageResult] as
      | CorruptPlan[]
      | CorruptAsset[]
      | undefined;

  // console.log('corruptItems', corruptItems);

  if (!success && corruptItems?.length) {
    return (
      <>
        <Alert
          severity="error"
          icon={<ErrorIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          Package bevat fouten in de {config.entityLabel}. Corrigeer deze voordat je verdergaat.
        </Alert>

        {corruptItems.map((item: any, index: number) => {
          const id =
            type === GeoPackageType.ASSET
              ? item.assetId
              : item.planId;

          const errors =
            type === GeoPackageType.ASSET
              ? item.assetErrors
              : item.planErrors;

          return (
            <Accordion
              key={index}
              sx={{
                backgroundColor: '#f9f9f9',
                boxShadow: 'none',
                mb: '5px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {type === GeoPackageType.ASSET ? 'Asset' : 'Plan'} ID: {id}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  {errors.map((error: string, i: number) => (
                    <li key={i}>
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </>
    );
  }

  if (!success && packageErrors?.length) {
    return (
      <>
        <Alert 
          severity="error" 
          icon={<ErrorIcon fontSize="inherit" />}
          sx={{ mb: 2 }}>
          Er zijn fouten gevonden in het bestand. De controle is niet voltooid.
        </Alert>
  
        <Accordion 
          sx={{
            backgroundColor: '#f9f9f9',
            boxShadow: 'none', // removes shadow
            mb: '5px',          // spacing between accordions
            '&:before': {
              display: 'none'  // removes default dividing line above
            }
          }} 
          defaultExpanded
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Bestandsfouten ({packageErrors?.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {packageErrors.map((e: any, index: number) => (
                <li key={index}>
                  <Typography variant="body2" color="error">
                    {e.error}
                  </Typography>
                </li>
              ))}
            </ul>
          </AccordionDetails>
        </Accordion>
      </>
    );
  }

  if (
    !success &&
    (!packageErrors || packageErrors.length === 0) &&
    (!corruptItems || corruptItems.length === 0)
  ) {
    return (
      <Alert
        severity="error"
        icon={<ErrorIcon fontSize="inherit" />}
        sx={{ mb: 2 }}
      >
        Validatie is niet succesvol, maar er zijn geen foutdetails ontvangen om weer te geven (deze data ontbreekt).
      </Alert>
    );
  }
  

  /* =========================
     SUCCESS VIEW
  ========================= */

  const sections = result ? getSections(result, type) : [];

  return (
    <>
      <Alert severity="success" 
        icon={<CheckCircle fontSize="inherit" />}
        sx={{ mb: 2 }}>
        Package is succesvol gecontroleerd.
      </Alert>

      {sections.map((section, index) => (
        <Accordion 
          key={index} 
          disabled={section.count === 0}
          sx={{
            backgroundColor: '#f9f9f9',
            boxShadow: 'none', // removes shadow
            mb: '5px',          // spacing between accordions
            '&:before': {
              display: 'none'  // removes default dividing line above
            }
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {section.label} ({section.count})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {section.items.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {section.items.map((id: string, idx: number) => (
                    <Chip key={idx} label={id} size="small" />
                  ))}
                </Box>
                {/* <ul style={{ paddingLeft: 16, margin: 0 }}>
                   {section.items.map((id: string, index: number) => (
                     <li key={index}>
                       <Typography variant="body2">{id}</Typography>
                     </li>
                  ))}
                </ul> */}
              </>
            ) : (
              <Typography variant="body2">Geen items</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default GeoPackageValidationResult;
